import { Dexie } from 'dexie';
import { AUTH_SESSIONS_INDEX_DB } from 'src/constants/authorization';
import AppUtils from 'src/utils/AppUtils';
import { BACKUP_DATA, BACKUP_NOTES } from 'src/utils/backupUtils';
import { logEvent } from 'src/utils/LogEvent';

const db = new Dexie('hi-rasmus', { allowEmptyDB: true });

const schema = {
  /* eslint-disable max-len */
  activities: 'id, client, [client+createTimestamp], [client+event], [client+event+createTimestamp], [client+event+eventInfo.programId]',

  appointments: 'id, clinic, [clinic+user], [clinic+session], [clinic+user+session], [clinic+client+status], [clinic+client+user+status], [clinic+user+status]',

  clinics: 'id',

  clinicSettings: 'id',
  clinicSettingsUpdates: 'id',
  billing: 'id',
  sub_userPrivileges: 'id', // id: 'idxDb_${clinicSettingsDocId}_{userPrivilegeId}'
  sub_treatmentAreas: 'id', // id: 'idxDb_${clinicSettingsDocId}_{treatmentAreaId}'
  sub_units: 'id', // id: 'idxDb_${clinicSettingsDocId}_{unitId}'
  sub_tags: 'id', // id: 'idxDb_${clinicSettingsDocId}_{tagId}'
  sub_dataFields: 'id', // id: 'idxDb_${clinicSettingsDocId}_{dataFieldId}'
  sub_sessionPrototypes: 'id', // id: 'idxDb_${clinicSettingsDocId}_{sessionPrototypeId}'
  sub_activityTypes: 'id', // id: 'idxDb_${clinicSettingsDocId}_{activityTypeId}'

  treatmentServices: 'id, clinic',
  placesOfService: 'id, clinic',

  clients: 'id, user, client, clinic, [clinic+type+user]',
  sub_taskLists: 'id, status', // id: 'idxDb_{clientId}_{taskListId}'

  data: 'id, session, [client+sessionProgram], [client+program], [client+session], [client+dataType]',

  dataFields: 'id',

  dataNotes: 'id, [client+session], [client+program+session]',

  programs: 'id, client, [clinic+client], [clinic+parentFolder], [client+programName], [package+programName], [package+client+clinic]',
  sub_programAutomationActions: 'id, order', // id: 'idxDb_{programId}_{programAutomationActionId}'
  sub_programBehaviourDataGroups: 'id, order', // id: 'idxDb_{programId}_{programBehaviourDataGroupId}'
  sub_sub_programBehaviourDataItems: 'id, order', // id: 'idxDb_{programId}_{programBehaviourDataGroupId}_{programBehaviourDataItemId}'
  sub_programFiles: 'id, programFileType, order', // id: 'idxDb_{programId}_{programFileId}'
  sub_programTargets: 'id, parentTarget, status', // id: 'idxDb_{programId}_{programTargetId}'
  sub_programFormItems: 'id, order', // id: 'idxDb_{programId}_{programFormItemId}'

  promptLevelTemplates: 'id, clinic, promptLevelTemplate',
  sub_promptLevels: 'id, order', // id: 'idxDb_{promptLevelTemplateId}_{promptLevelId}'

  sessions: 'id, client, status, [clinic+sessionNoteTemplate], [client+originalSessionId], [client+supervisedBy], [client+status], [clinic+status], [client+status+supervisedBy]',
  sub_sessionPrograms: 'id, order', // id: 'idxDb_{sessionId}_{sessionProgramId}'
  sub_sessionFiles: 'id',

  sessionActivities: 'id, session, status, [status+user], [client+session], [clinic+sessionNoteTemplateId], [clinic+session], [clinic+user], [clinic+client], [clinic+client+session], [clinic+client+user], [session+status+type], [clinic+client+session+user+appointment+status], [client+clinic+session+status+type], [clinic+client+session+user+status], [clinic+user+status]',

  sessionNoteTemplates: 'id, clinic',

  sessionNoteFields: 'id, clinic',

  signatures: 'id',

  treatmentAreas: 'id',

  messages: 'id, [clinic+client+session+type+signatoryId], [session+type+signatoryId], [clinic+client+thread]',

  users: 'id, clinic, [clinic+role]',

  userSettings: 'id, settings',
  sub_userSettings: 'id', // id: 'idxDb_{usersDocId}_settings'

  usersClients: 'id, user, client, [client+role], [client+role+user]',

  workflows: 'id, clinic',

  dbChanges: 'id, documentId, timestamp',

  filesUploading: 'id',
  filesUpdating: 'id, filePath',
  filesStorage: 'id',

  encryptionKeys: 'id, type, value',
  backupData: 'id, session, client',
  backupNotes: 'id, session, client',
  /* eslint-enable max-len */

  authSessions: 'id',
};

db.version(34).stores(schema);

async function deleteIndexedDb() {
  try {
    await db.delete();
    // Remove last sync data so that it would be re-synced on auth
    localStorage.removeItem('lastSyncData');
  } catch (err) {
    console.error(`IndexedDb delete failed: ${err}`);
    logEvent(err);
  }
}

async function openIndexedDb(noRetry = false) {
  try {
    return await db.open();
  } catch (err) {
    console.error(`IndexedDb open failed: ${err}`);
    logEvent(err);

    if (err.name === Dexie.errnames.Version && !noRetry) {
      console.error('IndexedDb version error - resetting IndexedDb');
      await deleteIndexedDb();
      return openIndexedDb(true);
    }
  }
}

function isIndexedDbOpened() {
  return db.isOpen();
}

async function clearOfflineModeData() {
  const deleteActions = db.tables
    .filter((table) => !['encryptionKeys', BACKUP_DATA, BACKUP_NOTES, AUTH_SESSIONS_INDEX_DB].includes(table.name))
    .map(async (table) => {
      return table.clear();
    });

  return AppUtils.allSettled(deleteActions);
}

async function clearIndexedDb() {
  const deleteActions = db.tables
    .map(async (table) => {
      return table.clear();
    });

  localStorage.removeItem('lastSyncData');

  return AppUtils.allSettled(deleteActions);
}

openIndexedDb();

async function clearIndexedDbTable(tableName) {
  return db.table(tableName).clear();
}

export default db;

export {
  clearIndexedDbTable,
  isIndexedDbOpened,
  clearOfflineModeData,
  clearIndexedDb,
  schema as indexedDbSchema,
};
